/**
 * Chart spec — the validation contract, with NO React and no renderer.
 *
 * Split out from the renderer on purpose: the spec is validated on the SERVER
 * (Meridian's `create_artifact` tool rejects a malformed chart before it can
 * reach a user), and a server tool must not pull a React component graph into
 * its import path. Importing this file gets you zod and nothing else.
 *
 * Rendered by `./ChartRenderer`.
 */

import { z } from 'zod';

export const NumberFormatSchema = z.enum(['plain', 'usd', 'pct']);
export type NumberFormat = z.infer<typeof NumberFormatSchema>;

export const SeriesSchema = z.object({
    name: z.string().min(1),
    values: z.array(z.number()).min(1),
    /** CSS color or var(...) — defaults to the brand palette by index. */
    color: z.string().optional()
});

export const AxisChartSchema = z.object({
    title: z.string().optional(),
    caption: z.string().optional(),
    categories: z.array(z.string().min(1)).min(1),
    series: z.array(SeriesSchema).min(1),
    yLabel: z.string().optional(),
    valueFormat: NumberFormatSchema.default('plain'),
    /** Draw value labels on/above each point or bar. Default true. */
    showValues: z.boolean().default(true)
});

export const BarChartSchema = AxisChartSchema.extend({ type: z.literal('bar') });
export const LineChartSchema = AxisChartSchema.extend({ type: z.literal('line') });

export const DonutChartSchema = z.object({
    type: z.literal('donut'),
    title: z.string().optional(),
    caption: z.string().optional(),
    segments: z
        .array(
            z.object({
                label: z.string().min(1),
                value: z.number().nonnegative(),
                color: z.string().optional()
            })
        )
        .min(1),
    centerLabel: z.string().optional(),
    valueFormat: NumberFormatSchema.default('plain')
});

export const ChartSpecSchema = z.discriminatedUnion('type', [
    BarChartSchema,
    LineChartSchema,
    DonutChartSchema
]);

export type ChartSpec = z.infer<typeof ChartSpecSchema>;

